import { ApplicationStatusEnum } from '@/app/common/general-types/briefs.dto';
import { PayoutStatusEnum } from '@/app/common/general-types/payouts.dto';
import {
  PayoutProviderEnum,
  WalletTransactionStatusEnum,
  WalletTransactionTypeEnum,
} from '@/app/common/general-types/wallet-transaction.dto';

const APPLICATION_STATUS_LABELS: Record<ApplicationStatusEnum, string> = {
  [ApplicationStatusEnum.All]: 'All',
  [ApplicationStatusEnum.Invited]: 'Invited',
  [ApplicationStatusEnum.Applied]: 'Applied',
  [ApplicationStatusEnum.Hired]: 'Hired',
  [ApplicationStatusEnum.ReadyToPost]: 'Ready to post',
  [ApplicationStatusEnum.Posted]: 'Posted',
  [ApplicationStatusEnum.Completed]: 'Completed',
  [ApplicationStatusEnum.Paid]: 'Paid',
  [ApplicationStatusEnum.Started]: 'Started',
  [ApplicationStatusEnum.InviteDeclined]: 'Invite declined',
  [ApplicationStatusEnum.ApplicationDeclined]: 'Application declined',
  [ApplicationStatusEnum.InviteRemoved]: 'Invite removed',
  [ApplicationStatusEnum.BonusPaid]: 'Bonus paid',
  [ApplicationStatusEnum.HiredRemoved]: 'Hired removed',
  [ApplicationStatusEnum.BriefingVQAExtracted]: 'Briefing VQA extracted',
  [ApplicationStatusEnum.AutoPilotOn]: 'Auto pilot on',
  [ApplicationStatusEnum.AutoPilotOff]: 'Auto pilot off',
};

const WALLET_TRANSACTION_TYPE_LABELS: Record<WalletTransactionTypeEnum, string> = {
  [WalletTransactionTypeEnum.WalletTopUp]: 'Wallet top-up',
  [WalletTransactionTypeEnum.Purchase]: 'Purchase',
  [WalletTransactionTypeEnum.Refund]: 'Refund',
  [WalletTransactionTypeEnum.SubscriptionTopUp]: 'Subscription top-up',
  [WalletTransactionTypeEnum.VoucherTopUp]: 'Voucher top-up',
};

const WALLET_TRANSACTION_STATUS_LABELS: Record<WalletTransactionStatusEnum, string> = {
  [WalletTransactionStatusEnum.Pending]: 'Pending',
  [WalletTransactionStatusEnum.Paid]: 'Paid',
};

export function applicationStatusLabel(status: ApplicationStatusEnum | number | undefined): string {
  if (status == null || status === undefined) {
    return '—';
  }
  return APPLICATION_STATUS_LABELS[status as ApplicationStatusEnum] ?? `Unknown (${status})`;
}

export function walletTransactionTypeLabel(type: WalletTransactionTypeEnum | number | undefined | null): string {
  if (type == null) {
    return '—';
  }
  return WALLET_TRANSACTION_TYPE_LABELS[type as WalletTransactionTypeEnum] ?? `Unknown (${type})`;
}

export function walletTransactionStatusLabel(status: WalletTransactionStatusEnum | number | undefined | null): string {
  if (status == null) {
    return '—';
  }
  return WALLET_TRANSACTION_STATUS_LABELS[status as WalletTransactionStatusEnum] ?? `Unknown (${status})`;
}

const PAYOUT_STATUS_LABELS: Partial<Record<PayoutStatusEnum, string>> = {
  [PayoutStatusEnum.Created]: 'Created',
  [PayoutStatusEnum.Succeeded]: 'Succeeded',
  [PayoutStatusEnum.Failed]: 'Failed',
  [PayoutStatusEnum.ProviderPreparing]: 'Provider preparing',
  [PayoutStatusEnum.AwaitingCmApproval]: 'Awaiting CM approval',
  [PayoutStatusEnum.AwaitingApproval]: 'Awaiting approval',
  [PayoutStatusEnum.AwaitingFunds]: 'Awaiting funds',
  [PayoutStatusEnum.AwaitingUserConfirmation]: 'Awaiting user confirmation',
  [PayoutStatusEnum.Sent]: 'Sent',
  [PayoutStatusEnum.InsufficientFunds]: 'Insufficient funds',
};

export function payoutStatusLabel(status: PayoutStatusEnum | number | undefined | null): string {
  if (status == null) {
    return '—';
  }
  return PAYOUT_STATUS_LABELS[status as PayoutStatusEnum] ?? `Unknown (${status})`;
}

const PAYOUT_STATUS_PILL_BASE =
  'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset';

const PAYOUT_STATUS_PILL_AMBER = `${PAYOUT_STATUS_PILL_BASE} bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-700/40`;
const PAYOUT_STATUS_PILL_GREEN = `${PAYOUT_STATUS_PILL_BASE} bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-200 dark:ring-green-700/40`;
const PAYOUT_STATUS_PILL_RED = `${PAYOUT_STATUS_PILL_BASE} bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-700/40`;

const PAYOUT_STATUS_PILL_AMBER_VALUES = new Set([0, 1, 4, 5, 6, 7, 8]);
const PAYOUT_STATUS_PILL_GREEN_VALUES = new Set([2, 10]);
const PAYOUT_STATUS_PILL_RED_VALUES = new Set([3, 11]);

export function payoutStatusPillClass(status: PayoutStatusEnum | number | undefined | null): string {
  if (status == null) {
    return PAYOUT_STATUS_PILL_AMBER;
  }
  const numericStatus = Number(status);
  if (PAYOUT_STATUS_PILL_GREEN_VALUES.has(numericStatus)) {
    return PAYOUT_STATUS_PILL_GREEN;
  }
  if (PAYOUT_STATUS_PILL_RED_VALUES.has(numericStatus)) {
    return PAYOUT_STATUS_PILL_RED;
  }
  if (PAYOUT_STATUS_PILL_AMBER_VALUES.has(numericStatus)) {
    return PAYOUT_STATUS_PILL_AMBER;
  }
  return PAYOUT_STATUS_PILL_AMBER;
}

export function payoutProviderLabel(provider: PayoutProviderEnum | string | undefined | null): string {
  if (provider == null || provider === '') {
    return '—';
  }
  return String(provider);
}
